import { ChartFrame } from 'components/ChartFrame'
import { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useElementSize } from 'usehooks-ts'
import { scaleLinear, groups, line } from 'd3'
import { global_locales } from 'components/constants'
import { useDate } from 'Globals/useDate'

const xTicks = [1, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50]
const svgHeight = 400
const compareYear = 2022
const highlightYear = 2026
const locales = global_locales.influenzaseasons

export const InluenzaSeason = ({
  colorMode = 'light',
  lang = 'de',
}: {
  colorMode?: 'dark' | 'light' | undefined
  lang?: 'de' | 'fr'
}) => {
  const [data, setData] = useState<any[] | undefined>()
  const [yMax, setYMax] = useState<number>(0)
  const [yTicks, setYTicks] = useState<number[]>([ 1000, 2000, 3000, 4000])
  const [containerWidth, setContainerWidth] = useState<number>(300)
  const [containerHeight, setContainerHeight] = useState<number>(svgHeight)
  const [hoveredCurve, setHoveredCurve] = useState<number | undefined>()
  const [containerRef, { width, height }] = useElementSize()
  const svgRef = useRef<SVGSVGElement>(null)
  const endDate = useDate()
  const [toolTipData, setToolTipData] = useState<TooltipData | undefined>(
    undefined
  )
  useEffect(() => {
    setContainerWidth(width || 100)
  }, [width])

  useEffect(() => {
    fetch(
      'https://storage.googleapis.com/ta-interaktiv/corona-dashboard-2023/influenza_detection.json?a=' +
        Math.random()
    )
      .then((a) => a.json())
      .then((a) => {
        var pre_format = a.map((m: any) => {
          m.year = +('' + m.week).substring(0, 4)
          m.week = +('' + m.week).substring(4, 6)
          var season = m.week > 25 ? m.year : m.year - 1
          var season_pos = m.week > 25 ? m.week - 25 : m.week + 27
          m.season = season
          m.season_pos = season_pos
          return m
        })
        var grouped_by_year = groups(pre_format, (d: any) => d.season)

        setData(grouped_by_year.sort((a, b) => a[0] - b[0]))
      })
  }, [])

  const drawArea = useMemo(
    () => ({
      top: 30,
      left: 50,
      right: containerWidth,
      bottom: containerHeight - 30,
    }),
    [containerWidth, containerHeight, data]
  )

  const x = useMemo(() => {
    return scaleLinear().domain([0, 52]).range([drawArea.left, drawArea.right])
  }, [drawArea.left, drawArea.right, endDate])

  const y = useMemo(() => {
    return scaleLinear()
      .domain([0, 4000])
      .range([drawArea.bottom, drawArea.top])
  }, [drawArea.bottom, drawArea.top, yMax])

  function prepTooltipData(e: any) {
    const chartx = e.clientX ? e.clientX : e.touches[0].clientX

    var hoveredType = e.target.tagName
    if (hoveredType !== 'path') {
      setHoveredCurve(undefined)
      return
    }
    const year = +e.target.classList[0]?.split('_')[1]
    setHoveredCurve(year)

    var date = Math.round(
      x.invert(
        chartx -
          (svgRef.current ? svgRef.current?.getBoundingClientRect().x : 0)
      )
    )

    var season_pos = date < 0 ? date + 50 : date
    const yeardata = data?.find((f: any) => f[0] === year)
    if (!yeardata) return
    const weekdata = yeardata[1].find((f: any) => f.season_pos === season_pos)

    if (weekdata) {
      setToolTipData({ x: weekdata.start_date, date, day: weekdata })
    }
  }
  const path = line()
    .x((d: any) => x(d.season_pos))
    .y((d: any) => y(d.detections))

  return (
    <ChartFrame
      lang={lang}
      title={locales[lang].title}
      timestamp={
        <>
          {new Date(endDate).toLocaleDateString(
            lang === 'fr' ? 'fr-CH' : 'de-CH',
            {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            }
          )}
        </>
      }
      lead={locales[lang].lead}
      source={locales[lang].source}
      colorMode={colorMode}
    >
      <VirusOverTimeRoot ref={containerRef}>
        <SVG
          colorMode={colorMode}
          ref={svgRef}
          width={'100%'}
          height={containerHeight + 'px'}
          onMouseMove={prepTooltipData}
          onTouchStart={prepTooltipData}
          onMouseOut={() => {
            setToolTipData(undefined)
          }}
        >
          <g className='xTicks'>
            {xTicks.map((date) => {
              return (
                <g
                  transform={`translate(${x(new Date(date))},${
                    drawArea.bottom
                  })`}
                >
                  <line y1={10} y2={0} stroke='var(--text-color)' />

                  <text
                    textAnchor='middle'
                    y={26}
                    fill={colorMode === 'dark' ? 'white' : '#202346'}
                  >
                    {date - 25 < 0 ? 25 + date : date - 25}
                  </text>
                </g>
              )
            })}
          </g>
          <g className='yTicks'>
            {yTicks.map((val) => {
              return (
                <g transform={`translate(0,${y(val)})`}>
                  <line x2={drawArea.left - 4} stroke='var(--text-color)' />
                  <line
                    x2={drawArea.left - 4}
                    x1={drawArea.right}
                    stroke='var(--text-color)'
                    opacity={val === 0 ? 1 : 0.1}
                  />
                  <text
                    x={drawArea.left - 4}
                    y={16}
                    textAnchor='end'
                    fill={colorMode === 'dark' ? 'white' : '#202346'}
                  >
                    {val}
                  </text>
                </g>
              )
            })}
          </g>{' '}
          <line
            x1={0}
            x2={containerWidth}
            y1={drawArea.bottom}
            y2={drawArea.bottom}
            stroke='var(--text-color)'
          />
          {data &&
            data
              .sort((a: any, b: any) => {
                if (hoveredCurve) {
                  if (a[0] === hoveredCurve) return 1
                  if (b[0] === hoveredCurve) return -1
                }

                return a[0] - b[0]
              })
              .map((d: any) => {
                return (
                  <>
                    <path
                      className={'y_' + d[0]}
                      d={path(d[1]) as string}
                      stroke={
                        hoveredCurve
                          ? d[0] === hoveredCurve
                            ? 'var(--red-main)'
                            : '#ccc'
                          : d[0] === highlightYear
                          ? 'var(--red-main)'
                          : d[0] === compareYear
                          ? 'var(--orange-bright-1'
                          : 'var(--gray-4)'
                      }
                      fill='none'
                      strokeWidth={
                        hoveredCurve
                          ? d[0] === hoveredCurve
                            ? 2
                            : 1
                          : d[0] === highlightYear
                          ? 3
                          : d[0] === compareYear
                          ? 1.5
                          : 1
                      }
                    />

                    <path
                      className={'y_' + d[0]}
                      d={path(d[1]) as string}
                      stroke={'#f00'}
                      strokeOpacity={0}
                      fill='none'
                      strokeWidth={12}
                    />
                    {d[0] === highlightYear && !toolTipData && (
                      <Annotation
                        important
                        color={'var(--red-main)'}
                        x={x(d[1][d[1].length - 1].season_pos)}
                        y={y(d[1][d[1].length - 1].detections)}
                        value={d[1][d[1].length - 1].detections}
                        year={highlightYear + '/' + (highlightYear + 1)}
                        textAnchor='start'
                      />
                    )}
                    {d[0] === 2022 && !toolTipData && (
                      <Annotation
                        color={'var(--orange-bright-1)'}
                        x={x(d[1][Math.round(d[1].length / 2)].season_pos)}
                        y={y(d[1][Math.round(d[1].length / 2)].detections)}
                        value={d[1][Math.round(d[1].length / 2)].detections}
                        year={compareYear + '/' + (compareYear + 1)}
                      />
                    )}
                  </>
                )
              })}
          {toolTipData && data && (
            <Tooltip
              colorMode={colorMode}
              data={toolTipData}
              drawArea={drawArea}
              width={width || 100}
              x={x(toolTipData.day.season_pos)}
              y={y(toolTipData.day.detections)}
              lang={lang}
            />
          )}
        </SVG>
      </VirusOverTimeRoot>
    </ChartFrame>
  )
}

const Annotation = ({
  x,
  y,
  value,
  year,
  color,
  important = false,
  textAnchor = 'end',
}: {
  x: number
  y: number
  value?: number
  year?: number | string
  color: string
  important?: boolean
  textAnchor?: 'start' | 'end' | 'middle'
}) => {
  return (
    <g transform={`translate(${x},${y - 5})`} className='annotation'>
      {value && (
        <Anno y={-32} x={2} textAnchor={textAnchor} fill='var(--text-color)'>
          {value}
        </Anno>
      )}
      {year && (
        <YearAnno y={value ? -48 : -32} x={2} textAnchor={textAnchor} fill={color}>
          {year}
        </YearAnno>
      )}
      <line y2={-24} strokeWidth={0.75} stroke={'var(--text-color)'} />
      {/* <circle r={important ? 6 : 4} fill={color} /> */}
      {/* {important && <circle r={3} fill='var(--site-background)' />} */}
    </g>
  )
}

const Anno = styled.text`
  font-size: 15px;
  font-weight: 700;
`
const YearAnno = styled.text`
  font-size: 14px;
  font-weight: 300;
`
type TooltipData = {
  x: string
  date: number
  day: any
}

const distance = 50

const Tooltip = ({
  data,
  drawArea,
  width,
  x,
  y,
  colorMode,
  lang,
}: {
  data: TooltipData
  drawArea: {
    top: number
    left: number
    right: number
    bottom: number
  }
  width: number
  x: number
  y: number
  colorMode: 'dark' | 'light' | undefined
  lang: 'de' | 'fr'
}) => {
  const { date, day } = data
  const date_width = 160
  const direction: 'left' | 'right' = x > width * 0.5 ? 'right' : 'left'

  const align =
    direction === 'left' ? 'left' : direction === 'right' ? 'right' : 'center'

  return (
    <g
      className='tooltip'
      transform={`translate(${x},${y - drawArea.top - distance})`}
    >
      <foreignObject
        x={direction === 'left' ? 0 : direction === 'right' ? -date_width : 0}
        y={0}
        width={date_width}
        height={60}
      >
        <body>
          <div
            style={{
              textAlign: align,
            }}
          >
            <div className='element'>
              <TTDate
                style={{
                  textAlign: align,
                }}
              >
                {new Date(
                  new Date(day.start_date).setDate(
                    new Date(day.start_date).getDate()
                  )
                ).toLocaleDateString(lang === 'fr' ? 'fr-CH' : 'de-CH', {
                  day: '2-digit',
                  // month: 'short',
                  // year: 'numeric',
                }) +
                  '. – ' +
                  new Date(
                    new Date(day.end_date).setDate(
                      new Date(day.end_date).getDate() + 3
                    )
                  ).toLocaleDateString(lang === 'fr' ? 'fr-CH' : 'de-CH', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}
              </TTDate>
              <VirusValue
                style={{
                  textAlign: align,
                }}
              >
                {day ? day.detections : 0}
              </VirusValue>
            </div>
          </div>
        </body>
      </foreignObject>
      <rect
        width={2}
        x={-1}
        height={distance + drawArea.top}
        fill={colorMode === 'dark' ? 'rgba(255,255,255,.75)' : 'rgba(0,0,0,.5)'}
      />{' '}
      <circle r={4} fill='var(--red-main)' cy={distance + drawArea.top} />
    </g>
  )
}
const TTDate = styled.div`
  display: inline-block;
  flex: 0 0 100%;
  font-weight: 300;
  margin-bottom: 10px;
  /* color: var(--red-main); */
`

const VirusValue = styled.div`
  flex: 0 0 100%;
  font-size: 18px;
  /* color: var(--red-main); */
`

const VirusOverTimeRoot = styled.div``

const SVG = styled.svg<{ colorMode: 'dark' | 'light' | undefined }>`
  overflow: visible;
  * {
    pointer-events: none;
  }
  path {
    pointer-events: visibleStroke;
  }
  foreignObject {
    font-weight: 700;
    font-size: 13px;
    line-height: 1em;
    div.element {
      display: flex;
      flex-wrap: wrap;
      /* justify-content: space-between; */
      background-color: var(--site-background);
      border-bottom: ${(props) =>
        props.colorMode === 'dark'
          ? '1px solid var(--gray-2)'
          : '1px solid rgba(255, 255, 255, 1)'};
      text-align: left;
      padding: 8px;
    }
  }
  .xTicks,
  .yTicks {
    font-size: 12px;
  }

  .tooltip {
    font-size: 14px;
  }
`

function addDays(date: string, days: number) {
  var result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}
